import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Calendar, Globe, Sprout, Star } from "lucide-react";
import { Profile } from "@/hooks/useProfile";
import { TrendsSummary } from "./TrendsSummary";
import { formatLocationForDisplay } from "@/utils/locationUtils";

interface FarmProfileProps {
  profile: Profile;
  userCropPlansCount?: number;
  userCropPlans?: Array<{
    id: string;
    commodity: string | null;
    variety: string | null;
    group_name: string | null;
    season: string | null;
    year: number;
    is_primary: boolean;
  }>;
}

export const FarmProfile = ({ profile, userCropPlansCount = 0, userCropPlans = [] }: FarmProfileProps) => {
  // Get images from farm_images array
  const farmImages = profile.farm_images && Array.isArray(profile.farm_images) ? profile.farm_images : [];
  const profileIcon = farmImages.find((img: any) => img.role === 'profile_icon' || img.is_logo);
  const mainFarmImage = farmImages.find((img: any) => img.role === 'main_farm');
  const additionalImages = farmImages.filter((img: any) => img.role === 'farm_extra');

  const avatarUrl = profileIcon?.url || profile.avatar_url;
  const mainImageUrl = mainFarmImage?.url || profile.farm_logo_url;

  // Get unique crops from user crop plans
  const activeCrops = userCropPlans.reduce((unique: string[], crop) => {
    const cropName = crop.commodity || crop.group_name || 'Unknown Crop';
    if (!unique.includes(cropName)) {
      unique.push(cropName);
    }
    return unique;
  }, []);

  return (
    <div className="space-y-3 px-3">
      {/* Main Profile Card - clean card design matching Discover */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {/* Cover Image */}
        {mainImageUrl && (
          <div className="h-40 bg-gradient-to-br from-green-400 to-green-600 relative">
            <img
              src={mainImageUrl}
              alt="Farm cover"
              className="w-full h-full object-cover"
            />
          </div>
        )}
        
        {/* Profile Content */}
        <div className="p-5">
          <div className="flex items-start space-x-3 mb-4">
            {/* Avatar */}
            <div className="w-14 h-14 rounded-full border-3 border-white shadow-md overflow-hidden flex-shrink-0 -mt-7 bg-white">
              {avatarUrl ? (
                <img 
                  src={avatarUrl} 
                  alt={`${profile.farm_name || profile.username}'s avatar`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-green-100 flex items-center justify-center">
                  <Sprout size={24} className="text-green-600" />
                </div>
              )}
            </div>
            
            <div className="flex-1 min-w-0 pt-0.5">
              <div className="flex items-center gap-1.5">
                <h3 className="font-bold text-gray-900 text-lg truncate">
                  {profile.farm_name || profile.username || 'Farm Profile'}
                </h3>
                <div className="text-green-600 text-lg">✓</div>
              </div>
              <p className="text-gray-500 text-sm">@{profile.username}</p>
              
              {profile.location && (
                <div className="flex items-center gap-1 text-green-600 text-sm mt-1">
                  <MapPin size={14} />
                  <span className="truncate">{formatLocationForDisplay(profile.location, "Location available")}</span>
                </div>
              )}
            </div>
          </div>

          {/* Zone badge and crops count */}
          <div className="flex items-center gap-2 mb-4">
            {profile.usda_zone && (
              <div className="bg-green-600 text-white rounded-full px-3 py-1 flex items-center gap-1.5">
                <img 
                  src="/lovable-uploads/c22db1b9-e0c9-405c-a328-60c97c2e4f7c.png" 
                  alt="Zone" 
                  className="w-3.5 h-3.5 object-cover rounded" 
                />
                <span className="text-xs font-semibold">Zone {profile.usda_zone.replace('Zone ', '')}</span>
              </div>
            )}
            <div className="text-gray-600 text-sm">
              {userCropPlansCount} active crops
            </div>
          </div>

          {/* Bio */}
          {profile.bio && (
            <p className="text-gray-700 text-sm leading-relaxed mb-4">{profile.bio}</p>
          )}

          {/* Profile Tags */}
          {profile.profile_tags && profile.profile_tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {profile.profile_tags.map((tag) => (
                <Badge
                  key={tag}
                  variant="secondary"
                  className="text-xs bg-blue-50 text-blue-700 hover:bg-blue-100"
                >
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          {/* Active Crops */}
          {activeCrops.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {activeCrops.map((crop) => (
                <span
                  key={crop}
                  className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-full"
                >
                  {crop}
                </span>
              ))}
            </div>
          )}

          {/* Farm Goals */}
          {profile.farm_goals && profile.farm_goals.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-bold text-gray-900 mb-2.5">Farm Goals</h4>
              <div className="flex flex-wrap gap-2">
                {profile.farm_goals.map((goal, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-md"
                  >
                    {goal}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Additional Farm Images */}
          {additionalImages.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-bold text-gray-900 mb-2.5">More Farm Photos</h4>
              <div className="grid grid-cols-2 gap-3">
                {additionalImages.slice(0, 4).map((image: any, index: number) => (
                  <div key={image.id || index} className="aspect-square rounded-xl overflow-hidden">
                    <img
                      src={image.url}
                      alt={`Farm view ${index + 1}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Farm Stats */}
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="p-3 bg-green-50 rounded-xl">
              <div className="text-2xl font-bold text-green-600">{userCropPlansCount}</div>
              <div className="text-xs text-gray-600 mt-0.5">Crops</div>
            </div>
            <div className="p-3 bg-blue-50 rounded-xl">
              <div className="text-lg font-bold text-blue-600">
                {profile.farm_size?.replace(' acres', '') || 'N/A'}
              </div>
              <div className="text-xs text-gray-600 mt-0.5">Size</div>
            </div>
            <div className="p-3 bg-orange-50 rounded-xl">
              <div className="text-xl font-bold text-orange-600">
                {profile.usda_zone ? profile.usda_zone.replace('Zone ', '').replace('a', '') : 'N/A'}
              </div>
              <div className="text-xs text-gray-600 mt-0.5">Zone</div>
            </div>
          </div>
        </div>
      </div>

      {/* Trends Summary Card */}
      <TrendsSummary userId={profile.user_id} />
    </div>
  );
};
